import React, { useState, useEffect, useCallback } from 'react';
import { knowledgeAPI } from '../../lib/api';
import { VoiceAgentTester } from '../../components/voice/VoiceAgentTester';
import KBCoveragePanel from '../../components/dashboard/KBCoveragePanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  Trash2, 
  RefreshCw,
  Brain,
  BookOpen,
  FileQuestion,
  Shield,
  Megaphone,
  Headphones,
  AlertCircle,
  CheckCircle,
  X,
  Search,
  Database,
  Globe,
  Languages,
  Link2,
  Eye,
  AlertTriangle,
  Loader2,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Send,
  Save
} from 'lucide-react';
import { format } from 'date-fns';

// Source type configuration
const sourceTypeConfig = {
  faq: { label: 'FAQ', icon: FileQuestion, color: 'bg-blue-500' },
  policy: { label: 'Policy', icon: Shield, color: 'bg-purple-500' },
  sales: { label: 'Sales', icon: Megaphone, color: 'bg-green-500' },
  support: { label: 'Support', icon: Headphones, color: 'bg-orange-500' },
};

// Phase M: Language configuration
const languageConfig = {
  en: { label: 'English', flag: '🇺🇸', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' },
  hi: { label: 'Hindi', flag: '🇮🇳', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300' },
  es: { label: 'Spanish', flag: '🇪🇸', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' },
};

export default function KnowledgeBase() {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showChunksDialog, setShowChunksDialog] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all'); // Phase M: Language filter
  
  // Upload form state (Phase M: Added language fields)
  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: '',
    sourceType: 'faq',
    languageCode: 'en',  // Phase M
    isTranslation: false,  // Phase M
    sourceParentId: null   // Phase M
  });

  // Iter 60 — search input + reprocess in-flight tracker + status-toggle confirm
  const [searchQuery, setSearchQuery] = useState('');
  const [reprocessing, setReprocessing] = useState({});
  const [pendingToggleSource, setPendingToggleSource] = useState(null);
  const [pageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  // Phase 4 — refresh-from-website + Test-the-KB
  const [refreshingWebsite, setRefreshingWebsite] = useState(false);
  const [health, setHealth] = useState(null);
  const [testQuery, setTestQuery] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [testing, setTesting] = useState(false);
  const [openSources, setOpenSources] = useState({});
  const [savingKb, setSavingKb] = useState({});
  const [savedKb, setSavedKb] = useState({});
  const chatScrollRef = React.useRef(null);

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMessages, testing]);

  // Add any URL / sitemap to the KB (additive)
  const [addUrl, setAddUrl] = useState('');
  const [addingUrl, setAddingUrl] = useState(false);

  const handleAddUrl = async () => {
    const u = addUrl.trim();
    if (!u) { toast.error('Enter a website or sitemap URL'); return; }
    setAddingUrl(true);
    try {
      const res = await knowledgeAPI.scrapeUrl(u);
      const pages = res.data?.pages_scraped ?? 0;
      if (pages > 0) {
        toast.success(`Added ${pages} page${pages !== 1 ? 's' : ''} from ${u} to your Knowledge Base`);
        if (res.data?.warning) {
          toast.warning(res.data.warning, { duration: 9000 });
        }
        setAddUrl('');
        loadData();
      } else {
        toast.warning('No readable content found at that URL.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not scrape that URL.');
    } finally {
      setAddingUrl(false);
    }
  };

  const handleRefreshWebsite = async () => {
    setRefreshingWebsite(true);
    try {
      const res = await knowledgeAPI.refreshWebsite();
      const pages = res.data?.pages_scraped ?? 0;
      if (pages > 0) toast.success(`Re-scraped ${pages} page${pages !== 1 ? 's' : ''} from ${res.data?.website || 'your website'}`);
      else toast.warning('No pages could be scraped — check the website URL.');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to refresh from website');
    } finally {
      setRefreshingWebsite(false);
    }
  };

  const handleTestKB = async () => {
    const q = testQuery.trim();
    if (!q) { toast.error('Type a question'); return; }
    if (testing) return;
    const history = chatMessages.map((m) => ({ role: m.role, content: m.content }));
    setChatMessages((prev) => [...prev, { role: 'user', content: q }]);
    setTestQuery('');
    setTesting(true);
    try {
      const res = await knowledgeAPI.testAnswer(q, 'en', history);
      const data = res.data || {};
      setChatMessages((prev) => [...prev, {
        role: 'assistant',
        content: data.answer || '',
        question: q,
        confidence: data.confidence,
        fallback_used: data.fallback_used,
        answered_from_expertise: data.answered_from_expertise,
        credit_charged: data.credit_charged,
        credit_cost_credits: data.credit_cost_credits,
        sources: data.sources || [],
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KB test failed');
      setChatMessages((prev) => [...prev, {
        role: 'assistant', content: "Sorry — I couldn't answer that just now. Please try again.", error: true,
      }]);
    } finally {
      setTesting(false);
    }
  };

  // KB Gap Autopilot — save a "general expertise" answer into the Knowledge Base
  const handleSaveToKb = async (idx) => {
    const m = chatMessages[idx];
    if (!m || savingKb[idx] || savedKb[idx]) return;
    setSavingKb((s) => ({ ...s, [idx]: true }));
    try {
      const res = await knowledgeAPI.saveAnswer(m.question || '', m.content || '');
      const chunks = res.data?.chunk_count ?? 0;
      toast.success(`Saved to your Knowledge Base${chunks ? ` (${chunks} chunk${chunks !== 1 ? 's' : ''})` : ''}`);
      setSavedKb((s) => ({ ...s, [idx]: true }));
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to Knowledge Base');
    } finally {
      setSavingKb((s) => ({ ...s, [idx]: false }));
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sourcesRes, statsRes, healthRes] = await Promise.all([
        knowledgeAPI.getSources(
          filterType !== 'all' ? filterType : null,
          filterStatus !== 'all' ? filterStatus : null,
          filterLanguage !== 'all' ? filterLanguage : null  // Phase M: Language filter
        ),
        knowledgeAPI.getStats(),
        knowledgeAPI.getHealth().catch(() => ({ data: null })),
      ]);
      setSources(sourcesRes.data || []);
      setStats(statsRes.data);
      setHealth(healthRes.data);
    } catch (error) {
      console.error('Failed to load KB data:', error);
      toast.error('Failed to load Knowledge Base data');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, filterLanguage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Auto-fill name from filename
      const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      setUploadForm(prev => ({
        ...prev,
        file,
        name: prev.name || fileName
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.name) {
      toast.error('Please select a file and enter a name');
      return;
    }

    try {
      setUploading(true);
      await knowledgeAPI.uploadSource(
        uploadForm.file,
        uploadForm.name,
        uploadForm.sourceType,
        uploadForm.languageCode,  // Phase M
        uploadForm.isTranslation,  // Phase M
        uploadForm.sourceParentId  // Phase M
      );
      
      toast.success(`Knowledge source uploaded successfully (${languageConfig[uploadForm.languageCode]?.label || 'English'})`);
      setShowUploadDialog(false);
      setUploadForm({ 
        file: null, 
        name: '', 
        sourceType: 'faq',
        languageCode: 'en',
        isTranslation: false,
        sourceParentId: null
      });
      loadData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload knowledge source');
    } finally {
      setUploading(false);
    }
  };

  const handleStatusToggle = async (source) => {
    // Iter 60 — confirm before disabling an active source (affects live calls)
    if (source.status === 'active') {
      setPendingToggleSource(source);
      return;
    }
    await applyStatusToggle(source);
  };

  const applyStatusToggle = async (source) => {
    const newStatus = source.status === 'active' ? 'disabled' : 'active';
    try {
      await knowledgeAPI.updateSourceStatus(source.id, newStatus);
      toast.success(`Source ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      console.error('Status update failed:', error);
      toast.error('Failed to update source status');
    } finally {
      setPendingToggleSource(null);
    }
  };

  // Iter 60 — repair sources that landed with chunk_count=0 (e.g. embedding
  // service was offline during onboarding scrape). Calls the new
  // POST /api/knowledge/sources/{id}/reprocess endpoint.
  const handleReprocess = async (source) => {
    setReprocessing((prev) => ({ ...prev, [source.id]: true }));
    try {
      const res = await knowledgeAPI.reprocessSource(source.id);
      const count = res.data?.chunk_count ?? 0;
      if (count > 0) toast.success(`Reprocessed — ${count} chunks indexed`);
      else toast.warning('Reprocess completed but produced 0 chunks. Re-upload may be required.');
      loadData();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to reprocess source';
      toast.error(msg);
    } finally {
      setReprocessing((prev) => ({ ...prev, [source.id]: false }));
    }
  };

  // Iter 60 — bulk delete after multi-select
  const handleBulkDelete = async () => {
    setShowBulkConfirm(false);
    let success = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        await knowledgeAPI.deleteSource(id);
        success += 1;
      } catch {
        failed += 1;
      }
    }
    if (success > 0) toast.success(`Deleted ${success} source${success !== 1 ? 's' : ''}`);
    if (failed > 0) toast.error(`Failed to delete ${failed} source${failed !== 1 ? 's' : ''}`);
    setSelectedIds([]);
    loadData();
  };

  const handleDelete = async () => {
    if (!selectedSource) return;
    
    try {
      await knowledgeAPI.deleteSource(selectedSource.id);
      toast.success('Knowledge source deleted');
      setShowDeleteDialog(false);
      setSelectedSource(null);
      loadData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete knowledge source');
    }
  };

  const handleViewChunks = async (source) => {
    setSelectedSource(source);
    setShowChunksDialog(true);
    setChunksLoading(true);
    
    try {
      const res = await knowledgeAPI.getSourceChunks(source.id);
      setChunks(res.data || []);
    } catch (error) {
      console.error('Failed to load chunks:', error);
      toast.error('Failed to load document chunks');
    } finally {
      setChunksLoading(false);
    }
  };

  const getSourceTypeIcon = (type) => {
    const config = sourceTypeConfig[type];
    if (!config) return FileText;
    return config.icon;
  };

  const getSourceTypeBadge = (type) => {
    const config = sourceTypeConfig[type] || { label: type, color: 'bg-gray-500' };
    return (
      <Badge className={`${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  // Phase M: Language badge component
  const getLanguageBadge = (languageCode) => {
    const config = languageConfig[languageCode] || languageConfig.en;
    return (
      <Badge 
        variant="outline" 
        className={`${config.color} font-medium`}
        data-testid={`language-badge-${languageCode}`}
      >
        <span className="mr-1">{config.flag}</span>
        {config.label}
      </Badge>
    );
  };

  // Phase M: Get parent source name for translations
  const getParentSourceName = (sourceParentId) => {
    if (!sourceParentId) return null;
    const parent = sources.find(s => s.id === sourceParentId);
    return parent?.name || 'Unknown';
  };

  // Iter 60 — derived: client-side search + pagination + "any source has a real
  // file" check (used to hide the File column entirely when nothing has one,
  // e.g. an all-scrape tenant).
  const filteredSources = sources.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.source_type || '').toLowerCase().includes(q) ||
      (s.file_name || '').toLowerCase().includes(q) ||
      (s.language_code || '').toLowerCase().includes(q)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredSources.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedSources = filteredSources.slice((safePage - 1) * pageSize, safePage * pageSize);
  const hasAnyFile = sources.some((s) => Boolean(s.file_name));
  const allOnPageSelected = pagedSources.length > 0 && pagedSources.every((s) => selectedIds.includes(s.id));

  return (
    <div className="p-6 space-y-6" data-testid="knowledge-base-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Brain className="h-8 w-8 text-primary" />
            Knowledge Base
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage multilingual knowledge sources for Voice AI responses
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefreshWebsite} disabled={refreshingWebsite} data-testid="refresh-website-btn">
            {refreshingWebsite ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
            {refreshingWebsite ? 'Re-scraping…' : 'Refresh from Website'}
          </Button>
          <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>

      {/* KB Coverage indicator + auto re-scrape schedule */}
      <KBCoveragePanel />

      {/* Embedding-degradation warning: semantic retrieval is silently weakened */}
      {health?.embedding_degraded && (
        <Alert variant="destructive" data-testid="kb-embedding-degraded-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Knowledge retrieval is degraded.</strong> The semantic embedding model failed to load, so the AI is using a much weaker keyword fallback and may not understand your uploaded documents accurately. {health?.embedding_degraded_reason ? <span className="opacity-80">({health.embedding_degraded_reason})</span> : null} Please contact support or retry — re-embed your KB once restored.
          </AlertDescription>
        </Alert>
      )}


      {/* Add any website / sitemap URL to the KB (additive) */}
      <Card data-testid="kb-add-url-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4 text-primary" />
            Add a Website or Sitemap
          </CardTitle>
          <CardDescription>
            Paste any public URL (a page, a docs site, or a <code>/sitemap.xml</code>) to teach the AI more about your business. This adds to your Knowledge Base without removing existing sources.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              value={addUrl}
              onChange={(e) => setAddUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddUrl(); }}
              placeholder="https://example.com/pricing  or  https://example.com/sitemap.xml"
              data-testid="kb-add-url-input"
            />
            <Button onClick={handleAddUrl} disabled={addingUrl} data-testid="kb-add-url-submit">
              {addingUrl ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Scraping…</> : <><Upload className="h-4 w-4 mr-2" />Add to KB</>}
            </Button>
          </div>
        </CardContent>
      </Card>




      {/* Phase 4 — Test the KB: see exactly what the voice agent would retrieve */}
      <Card data-testid="kb-test-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            Test the Knowledge Base
          </CardTitle>
          <CardDescription>
            Chat with your AI the way a customer would — it replies live, exactly as it would on a real call or chat.
            {health?.last_scraped && (
              <span className="ml-1 text-xs">Last scraped: {(() => { try { return format(new Date(health.last_scraped), 'PP'); } catch { return '—'; } })()}</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Chat transcript */}
          <div
            ref={chatScrollRef}
            className="h-72 overflow-y-auto rounded-xl border border-border bg-muted/20 p-3 space-y-3"
            data-testid="kb-chat-transcript"
          >
            {chatMessages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm">Ask your AI anything about your business.</p>
                <p className="text-xs">Try: “How much does BIS certification cost?”</p>
              </div>
            ) : (
              chatMessages.map((m, i) => (
                m.role === 'user' ? (
                  <div key={i} className="flex justify-end" data-testid={`kb-chat-user-${i}`}>
                    <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground">{m.content}</div>
                  </div>
                ) : (
                  <div key={i} className="flex justify-start" data-testid={`kb-chat-ai-${i}`}>
                    <div className="max-w-[85%] space-y-1.5">
                      <div className={`rounded-2xl rounded-bl-sm border px-3.5 py-2.5 text-sm ${m.error ? 'border-destructive/40 bg-destructive/5' : m.fallback_used ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-card'}`}>
                        <p className="leading-relaxed whitespace-pre-wrap">{m.content}</p>
                      </div>
                      {!m.error && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-[11px] text-muted-foreground">
                          {m.answered_from_expertise ? (
                            <Badge variant="outline" className="text-[10px]">General expertise (not in KB)</Badge>
                          ) : (!m.fallback_used && typeof m.confidence === 'number' && (
                            <Badge variant="secondary" className="text-[10px]">{(m.confidence * 100).toFixed(0)}% confidence</Badge>
                          ))}
                          <span data-testid={`kb-chat-credit-${i}`}>
                            {m.credit_charged
                              ? <>Charged ~{m.credit_cost_credits} credit{m.credit_cost_credits === 1 ? '' : 's'}</>
                              : <>No credit charged (no KB match)</>}
                          </span>
                          {m.sources && m.sources.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setOpenSources((s) => ({ ...s, [i]: !s[i] }))}
                              className="inline-flex items-center gap-0.5 hover:text-foreground transition-colors"
                              data-testid={`kb-chat-sources-toggle-${i}`}
                            >
                              {openSources[i] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              {openSources[i] ? 'Hide' : 'Show'} {m.sources.length} sources
                            </button>
                          )}
                          {m.answered_from_expertise && (
                            savedKb[i] ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`kb-chat-saved-${i}`}>
                                <CheckCircle className="h-3 w-3" /> Saved to KB
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleSaveToKb(i)}
                                disabled={savingKb[i]}
                                className="inline-flex items-center gap-0.5 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                                data-testid={`kb-chat-save-${i}`}
                                title="Add this answer to your Knowledge Base so the AI answers it from approved knowledge next time"
                              >
                                {savingKb[i] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                {savingKb[i] ? 'Saving…' : 'Save to Knowledge Base'}
                              </button>
                            )
                          )}
                        </div>
                      )}
                      {openSources[i] && m.sources && m.sources.map((r, j) => (
                        <div key={j} className="rounded-md border border-border/60 bg-background p-2 text-xs" data-testid={`kb-chat-source-${i}-${j}`}>
                          <div className="flex items-center justify-between mb-0.5">
                            <Badge variant="outline" className="text-[10px]">{r.source_name || 'Knowledge'}</Badge>
                            <Badge className="text-[10px]" variant={r.similarity_score >= 0.55 ? 'default' : 'secondary'}>{(r.similarity_score * 100).toFixed(0)}% match</Badge>
                          </div>
                          <p className="text-muted-foreground line-clamp-2">{(r.content || '').trimStart()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))
            )}
            {testing && (
              <div className="flex justify-start" data-testid="kb-chat-typing">
                <div className="rounded-2xl rounded-bl-sm border border-border bg-card px-3.5 py-2.5 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="flex gap-2">
            <Input
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleTestKB(); }}
              placeholder="Type a message…"
              disabled={testing}
              data-testid="kb-test-input"
            />
            <Button onClick={handleTestKB} disabled={testing || !testQuery.trim()} data-testid="kb-test-submit">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" />Send</>}
            </Button>
            {chatMessages.length > 0 && (
              <Button variant="outline" onClick={() => { setChatMessages([]); setOpenSources({}); }} disabled={testing} data-testid="kb-chat-clear">Clear</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <VoiceAgentTester />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_sources}</p>
                  <p className="text-sm text-muted-foreground">Total Sources</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-green-500/10">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_sources}</p>
                  <p className="text-sm text-muted-foreground">Active Sources</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-500/10">
                  <BookOpen className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_chunks}</p>
                  <p className="text-sm text-muted-foreground">Total Chunks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(stats.sources_by_type || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="capitalize text-muted-foreground">{type}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters - Phase M: Added language filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-end flex-wrap">
            <div className="w-48">
              <Label>Source Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                  <SelectItem value="policy">Policy</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Phase M: Language Filter */}
            <div className="w-48">
              <Label className="flex items-center gap-1">
                <Languages className="h-4 w-4" />
                Language
              </Label>
              <Select value={filterLanguage} onValueChange={setFilterLanguage}>
                <SelectTrigger data-testid="filter-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      All Languages
                    </div>
                  </SelectItem>
                  <SelectItem value="en">
                    <div className="flex items-center gap-2">
                      <span>🇺🇸</span> English
                    </div>
                  </SelectItem>
                  <SelectItem value="hi">
                    <div className="flex items-center gap-2">
                      <span>🇮🇳</span> Hindi
                    </div>
                  </SelectItem>
                  <SelectItem value="es">
                    <div className="flex items-center gap-2">
                      <span>🇪🇸</span> Spanish
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sources Table - Phase M: Added language column */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Knowledge Sources</CardTitle>
              <CardDescription>
                Multilingual documents that power Voice AI responses during calls
              </CardDescription>
            </div>
            {/* Iter 60 — search input + bulk delete */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search by name, type, file…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-8 h-9 w-64"
                  data-testid="kb-search-input"
                />
              </div>
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowBulkConfirm(true)}
                  data-testid="kb-bulk-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete {selectedIds.length}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Knowledge Sources</h3>
              <p className="text-muted-foreground mb-4">
                Upload documents in multiple languages to enable knowledge-grounded Voice AI responses.
              </p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload First Document
              </Button>
            </div>
          ) : filteredSources.length === 0 ? (
            <div className="text-center py-8" data-testid="kb-search-empty">
              <Search className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No sources match "{searchQuery}".</p>
            </div>
          ) : (
            <TooltipProvider>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allOnPageSelected}
                      onCheckedChange={(checked) => {
                        const ids = pagedSources.map((s) => s.id);
                        setSelectedIds((prev) => checked
                          ? Array.from(new Set([...prev, ...ids]))
                          : prev.filter((id) => !ids.includes(id))
                        );
                      }}
                      data-testid="kb-select-all"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Language</TableHead>
                  {hasAnyFile && <TableHead>File</TableHead>}
                  <TableHead className="text-center">Chunks</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedSources.map((source) => {
                  const TypeIcon = getSourceTypeIcon(source.source_type);
                  const parentName = getParentSourceName(source.source_parent_id);
                  // Iter 60 — flag empty active sources (active+0-chunks =
                  // broken state; Voice AI calls retrieve nothing).
                  const isEmptyActive = source.status === 'active' && (source.chunk_count || 0) === 0;
                  const isReprocessing = !!reprocessing[source.id];
                  return (
                    <TableRow key={source.id} data-testid={`source-row-${source.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(source.id)}
                          onCheckedChange={(checked) => {
                            setSelectedIds((prev) => checked
                              ? [...prev, source.id]
                              : prev.filter((id) => id !== source.id)
                            );
                          }}
                          data-testid={`kb-select-${source.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <span className="font-medium">{source.name}</span>
                            {source.has_unconfirmed_placeholders && (
                              <span
                                className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-300 align-middle"
                                title="This document still has [CONFIRM: …] placeholders. Edit it and replace them so your AI agent never quotes an unconfirmed detail."
                                data-testid={`kb-needs-confirm-${source.id}`}
                              >
                                <AlertTriangle className="h-3 w-3" /> Needs confirming
                              </span>
                            )}
                            {/* Phase M: Show translation linkage */}
                            {source.is_translation && parentName && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                <Link2 className="h-3 w-3" />
                                Translation of: {parentName}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getSourceTypeBadge(source.source_type)}</TableCell>
                      {/* Phase M: Language badge */}
                      <TableCell>
                        {getLanguageBadge(source.language_code || 'en')}
                      </TableCell>
                      {hasAnyFile && (
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {source.file_name || '—'}
                          </span>
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        {isEmptyActive ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="cursor-pointer border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                                onClick={() => handleViewChunks(source)}
                                data-testid={`kb-empty-warning-${source.id}`}
                              >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                0 — empty
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Active but no chunks indexed. Voice AI cannot retrieve from this source. Click "Reprocess" in actions.
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Badge
                            variant="outline"
                            className="cursor-pointer hover:bg-muted"
                            onClick={() => handleViewChunks(source)}
                          >
                            {source.chunk_count}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={source.status === 'active'}
                            onCheckedChange={() => handleStatusToggle(source)}
                            data-testid={`toggle-${source.id}`}
                          />
                          <span className={`text-sm ${
                            source.status === 'active' 
                              ? 'text-green-600' 
                              : 'text-muted-foreground'
                          }`}>
                            {source.status === 'active' ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {source.created_at 
                            ? format(new Date(source.created_at), 'MMM d, yyyy')
                            : '-'
                          }
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewChunks(source)}
                                data-testid={`view-chunks-${source.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">View chunks</TooltipContent>
                          </Tooltip>
                          {isEmptyActive && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReprocess(source)}
                                  disabled={isReprocessing}
                                  data-testid={`reprocess-${source.id}`}
                                >
                                  {isReprocessing
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : <RefreshCw className="h-4 w-4 text-amber-600" />}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Reprocess (re-chunk + re-embed)</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => {
                                  setSelectedSource(source);
                                  setShowDeleteDialog(true);
                                }}
                                data-testid={`delete-${source.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Delete source</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </TooltipProvider>
          )}
          {/* Iter 60 — pagination */}
          {filteredSources.length > pageSize && (
            <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground" data-testid="kb-pagination">
              <span>
                Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filteredSources.length)} of {filteredSources.length}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>Previous</Button>
                <span className="px-2 py-1 text-xs">Page {safePage} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Alert - Phase M: Updated text */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>How Multilingual KB Works:</strong> Voice AI detects the caller's language at the start of each call
          and retrieves knowledge ONLY in that language. Upload separate documents for each supported language 
          (English, Hindi, Spanish). If no knowledge is found in the caller's language, Voice AI will politely 
          offer to connect them with a human representative.
        </AlertDescription>
      </Alert>

      {/* Upload Dialog - Phase M: Added language fields */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Knowledge Source
            </DialogTitle>
            <DialogDescription>
              Upload a document to add to the Voice AI knowledge base.
              Supported formats: Markdown (.md), Text (.txt), PDF (.pdf)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="file">Document File *</Label>
              <Input
                id="file"
                type="file"
                accept=".md,.txt,.pdf"
                onChange={handleFileChange}
                data-testid="file-input"
              />
              {uploadForm.file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {uploadForm.file.name}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Source Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Product FAQ, Refund Policy"
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source Type *</Label>
                <Select 
                  value={uploadForm.sourceType} 
                  onValueChange={(val) => setUploadForm(prev => ({ ...prev, sourceType: val }))}
                >
                  <SelectTrigger data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="faq">
                      <div className="flex items-center gap-2">
                        <FileQuestion className="h-4 w-4" />
                        FAQ
                      </div>
                    </SelectItem>
                    <SelectItem value="policy">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Policy
                      </div>
                    </SelectItem>
                    <SelectItem value="sales">
                      <div className="flex items-center gap-2">
                        <Megaphone className="h-4 w-4" />
                        Sales
                      </div>
                    </SelectItem>
                    <SelectItem value="support">
                      <div className="flex items-center gap-2">
                        <Headphones className="h-4 w-4" />
                        Support
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Phase M: Language selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Languages className="h-4 w-4" />
                  Language *
                </Label>
                <Select 
                  value={uploadForm.languageCode} 
                  onValueChange={(val) => setUploadForm(prev => ({ ...prev, languageCode: val }))}
                >
                  <SelectTrigger data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">
                      <div className="flex items-center gap-2">
                        <span>🇺🇸</span> English
                      </div>
                    </SelectItem>
                    <SelectItem value="hi">
                      <div className="flex items-center gap-2">
                        <span>🇮🇳</span> Hindi
                      </div>
                    </SelectItem>
                    <SelectItem value="es">
                      <div className="flex items-center gap-2">
                        <span>🇪🇸</span> Spanish
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Phase M: Translation checkbox */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="isTranslation"
                checked={uploadForm.isTranslation}
                onCheckedChange={(checked) => setUploadForm(prev => ({ 
                  ...prev, 
                  isTranslation: checked,
                  sourceParentId: checked ? prev.sourceParentId : null
                }))}
                data-testid="is-translation-checkbox"
              />
              <Label 
                htmlFor="isTranslation" 
                className="text-sm font-normal cursor-pointer"
              >
                This is a translation of an existing document
              </Label>
            </div>

            {/* Phase M: Parent source selection (shown only if isTranslation) */}
            {uploadForm.isTranslation && (
              <div className="space-y-2 pl-6">
                <Label>Original Document (Parent)</Label>
                <Select 
                  value={uploadForm.sourceParentId || ''} 
                  onValueChange={(val) => setUploadForm(prev => ({ ...prev, sourceParentId: val }))}
                >
                  <SelectTrigger data-testid="parent-source-select">
                    <SelectValue placeholder="Select original document..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sources
                      .filter(s => s.language_code !== uploadForm.languageCode)
                      .map(source => (
                        <SelectItem key={source.id} value={source.id}>
                          <div className="flex items-center gap-2">
                            {getLanguageBadge(source.language_code || 'en')}
                            <span>{source.name}</span>
                          </div>
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Link this translation to its original document for reference tracking.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || !uploadForm.file || !uploadForm.name}
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Knowledge Source
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedSource?.name}"?
              This will remove all associated chunks and cannot be undone.
              {selectedSource?.language_code && (
                <span className="block mt-2">
                  Language: {languageConfig[selectedSource.language_code]?.label || 'English'}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Chunks Dialog - Phase M: Show language */}
      <Dialog open={showChunksDialog} onOpenChange={setShowChunksDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Document Chunks: {selectedSource?.name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              The document has been split into {chunks.length} chunks for semantic search.
              {selectedSource?.language_code && (
                <span className="ml-2">
                  {getLanguageBadge(selectedSource.language_code)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 py-4">
            {chunksLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
              </div>
            ) : chunks.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No chunks found
              </p>
            ) : (
              chunks.map((chunk, idx) => (
                <Card key={chunk.id} className="bg-muted/30">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Chunk {idx + 1}</Badge>
                        {chunk.language_code && chunk.language_code !== 'en' && (
                          getLanguageBadge(chunk.language_code)
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {chunk.content?.length || 0} chars
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">
                      {chunk.content}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChunksDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 60 — Disable-active confirm dialog */}
      <Dialog open={!!pendingToggleSource} onOpenChange={(open) => !open && setPendingToggleSource(null)}>
        <DialogContent data-testid="kb-disable-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Disable this knowledge source?
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium text-foreground">{pendingToggleSource?.name}</span> will
              no longer be retrieved by the Voice AI on live calls. You can re-enable it at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingToggleSource(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => applyStatusToggle(pendingToggleSource)}
              data-testid="kb-disable-confirm-btn"
            >
              Yes, disable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 60 — Bulk-delete confirm dialog */}
      <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <DialogContent data-testid="kb-bulk-delete-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete {selectedIds.length} knowledge source{selectedIds.length !== 1 ? 's' : ''}?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the selected sources and all their chunks. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              data-testid="kb-bulk-delete-confirm-btn"
            >
              Delete {selectedIds.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
